import { useCallback, useEffect, useState } from "react";
import api, { euro, fmtDate, todayISO, can } from "../lib/api";
import { useQuick } from "../components/Layout";
import { Dumbbell, LogOut, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function Gym() {
  const { openModal } = useQuick();
  const { user } = useAuth();
  const [date, setDate] = useState(todayISO());
  const [checkins, setCheckins] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/gym", { params: { date_from: date, date_to: date } });
      setCheckins(data);
    } catch { /* noop */ }
  }, [date]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("ll:data-changed", h);
    const t = setInterval(load, 15000); // aggiornamento automatico ogni 15s: gli ingressi si vedono subito
    return () => { window.removeEventListener("ll:data-changed", h); clearInterval(t); };
  }, [load]);

  const checkout = async (c) => {
    try {
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      await api.post(`/gym/${c.id}/checkout`, { checkout_time: `${pad(now.getHours())}:${pad(now.getMinutes())}` });
      toast.success(`Uscita registrata per ${c.patient_name}`);
      load();
    } catch { toast.error("Errore"); }
  };

  const del = async (c) => {
    if (!window.confirm("Eliminare questo ingresso?")) return;
    await api.delete(`/gym/${c.id}`);
    load();
  };

  const open = checkins.filter((c) => !c.checkout_time);

  return (
    <div className="space-y-4" data-testid="gym-page">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-2xl font-bold">Check-in palestra</h1>
        <input data-testid="gym-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="ml-auto rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white" />
        <button data-testid="gym-new" onClick={() => openModal("gym")} className="flex items-center gap-1.5 bg-emerald-600 text-white px-4 py-2.5 rounded-full text-sm font-medium hover:bg-emerald-700"><Plus size={16} /> Check-in palestra</button>
      </div>

      <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-5" data-testid="gym-present">
        <p className="text-[10px] uppercase tracking-widest text-emerald-500 mb-2">In palestra ora</p>
        {open.length === 0 ? <p className="text-sm text-emerald-700">Nessuno presente</p> : (
          <div className="flex flex-wrap gap-2">
            {open.map((c) => (
              <div key={c.id} data-testid={`gym-open-${c.id}`} className="flex items-center gap-2 bg-white rounded-full pl-4 pr-2 py-1.5 border border-emerald-200 shadow-sm">
                <Dumbbell size={14} className="text-emerald-500" />
                <span className="text-sm font-medium">{c.patient_name}</span>
                <span className="text-xs text-slate-400 font-mono">dalle {c.checkin_time}</span>
                <button data-testid={`gym-checkout-${c.id}`} onClick={() => checkout(c)} className="p-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700" title="Check-out"><LogOut size={13} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="px-5 pt-4 pb-1 flex items-center justify-between">
          <h2 className="font-heading font-bold" data-testid="gym-today-title">
            {date === todayISO() ? `Ingressi di oggi (${checkins.length})` : `Ingressi del ${fmtDate(date)} (${checkins.length})`}
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="gym-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-400 border-b border-slate-100">
                <th className="py-3 px-4">Paziente</th><th className="px-3">Entrata</th><th className="px-3">Uscita</th>
                <th className="px-3">Attività</th><th className="px-3">Importo</th><th className="px-3">Pagamento</th><th className="px-3 no-print"></th>
              </tr>
            </thead>
            <tbody>
              {checkins.map((c) => (
                <tr key={c.id} data-testid={`gym-row-${c.id}`} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="py-3 px-4 font-medium">{c.patient_name}</td>
                  <td className="px-3 font-mono">{c.checkin_time}</td>
                  <td className="px-3 font-mono">{c.checkout_time || <span className="text-emerald-600 text-xs font-medium">in corso</span>}</td>
                  <td className="px-3 text-slate-600">{c.activity || "—"}</td>
                  <td className="px-3">{euro(c.amount)}</td>
                  <td className="px-3">{c.paid ? <span className="text-emerald-600 text-xs font-medium">Pagato · {c.method}</span> : <span className="text-red-500 text-xs font-medium">Da pagare</span>}</td>
                  <td className="px-3">
                    <div className="flex gap-1">
                      {!c.checkout_time && <button data-testid={`gym-row-checkout-${c.id}`} onClick={() => checkout(c)} className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100"><LogOut size={14} /></button>}
                      {can(user, "palestra", "delete") && <button data-testid={`gym-delete-${c.id}`} onClick={() => del(c)} className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50"><Trash2 size={14} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
              {checkins.length === 0 && <tr><td colSpan={7} className="py-12 text-center text-slate-400" data-testid="gym-empty">Nessun ingresso per {fmtDate(date)}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
